import React, { useEffect, useState } from "react";
import PanelShell from "../../shared/PanelShell";
import { fmtCurrency, fmtNumber } from "../../shared/format";
import { getAgentValue } from "../../../api/conversation";
import HourlyRateControl from "./HourlyRateControl";

// TIME SAVED — Tier 4 (V6 §E.10). agent_value.time_saved with an EDITABLE hourly
// rate: changing it RE-FIRES fn_get_agent_value with p_hourly_rate (never multiplies
// client-side — the one derivation the spec allows). Lifted verbatim from the v1
// CoreTier so it survives that container's teardown.
//
// The function is modality-scoped as of V6, so the page passes `modality` and the
// recompute keeps it. The default rate lives on `assumptions.hourly_rate`, and the
// labour value leads because it is what the saved time is WORTH — the only reason the
// rate is adjustable at all.
const TimeSavedPanel = ({ panelKey = "voice_time_saved", say, agentValue, clientId, rangeStart, rangeEnd, modality = "voice", loading = false }) => {
  const [local, setLocal] = useState(agentValue);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLocal(agentValue); }, [agentValue]);

  const recompute = async (next) => {
    if (!clientId || !rangeStart || !rangeEnd) return;
    setSaving(true);
    try { setLocal(await getAgentValue(clientId, rangeStart, rangeEnd, next, modality)); }
    catch (e) { console.error("time_saved recompute:", e); }
    finally { setSaving(false); }
  };

  const ts = local?.time_saved || {};
  const wl = local?.workload || {};
  const voice = ts.voice || {};
  const chat = ts.chat || {};
  const assumptions = local?.assumptions || {};
  const defaultRate = assumptions.hourly_rate;
  // Show ONLY this page's channel (Phone-board Markup #8). The payload still carries
  // both time_saved.voice and time_saved.chat even when scoped, but "Hours on chat"
  // on the Phone page (and vice-versa) mixes two channels the rest of the page keeps
  // apart. This reverses Addendum 1 §5b.2's "render both channel figures" for a
  // per-modality page; the labour value + hours saved (the totals) still show.
  const isChat = modality === "chat";

  return (
    <PanelShell
      panelKey={panelKey}
      label="Time saved"
      say={say}
      hasData={ts.hours != null || wl.conversations != null}
      loading={loading}
    >
      {ts.labour_value != null && (
        <p className="gi-number text-emerald-600 dark:text-emerald-400">{fmtCurrency(ts.labour_value)}</p>
      )}

      {/* Hour figures as a row of stat blocks — number ABOVE label (user ruling
          2026-09-05, matching Workload), each carrying its basis below. The chat basis
          differs from voice — chat cannot use elapsed time (its median session is a
          24h timeout), so it is priced from visitor messages at a rate measured from
          this property's own calls, and the string says so verbatim. ONLY THIS PAGE'S
          CHANNEL is rendered (Markup #8): the payload still carries both time_saved.voice
          and .chat, but showing the other channel here mixes two channels the rest of the
          page keeps apart (this narrows Addendum 1 §5b.2's "render both" for a per-page view). */}
      {(ts.hours != null || voice.hours != null || chat.hours != null || ts.total_phone_hours != null) && (
        <div className="flex flex-wrap gap-x-10 gap-y-4 mt-3">
          {ts.hours != null && (
            <div className="flex flex-col">
              <span className="gi-number text-[#2175A8] dark:text-[#32B1FF] tabular-nums">{fmtNumber(ts.hours, true)}</span>
              <span className="gi-label text-gray-500 dark:text-gray-400 mt-0.5">hours saved</span>
              {/* Chat has no separate "total" to compare against (chat time is estimated
                  from visitor messages, not elapsed), so its basis rides this one number
                  instead of a duplicate "hours on chat" block that showed the same figure
                  again — hours saved and hours on chat were identical (markup #22). */}
              {isChat && chat.basis && (
                <span data-testid="time-saved-chat-basis" className="gi-body text-gray-500 dark:text-gray-400 mt-1 max-w-[15rem]">{chat.basis}</span>
              )}
            </div>
          )}
          {/* Phone: the DENOMINATOR — total time on all calls (transferred included),
              so "hours saved" reads against something and the two are visibly different.
              Replaces the old "hours on phone", which equalled hours saved on the phone
              page (the payload handed the same figure twice). Falls back to voice.hours
              only for a payload that predates total_phone_hours. */}
          {!isChat && ts.total_phone_hours != null && (
            <div data-testid="time-saved-total-phone" className="flex flex-col">
              <span className="gi-number text-gray-900 dark:text-gray-100 tabular-nums">{fmtNumber(ts.total_phone_hours, true)}</span>
              <span className="gi-label text-gray-500 dark:text-gray-400 mt-0.5">total phone hours</span>
              {voice.basis && <span className="gi-body text-gray-500 dark:text-gray-400 mt-1 max-w-[15rem]">{voice.basis}</span>}
            </div>
          )}
          {!isChat && ts.total_phone_hours == null && voice.hours != null && (
            <div data-testid="time-saved-voice" className="flex flex-col">
              <span className="gi-number text-gray-900 dark:text-gray-100 tabular-nums">{fmtNumber(voice.hours, true)}</span>
              <span className="gi-label text-gray-500 dark:text-gray-400 mt-0.5">hours on phone</span>
              {voice.basis && <span className="gi-body text-gray-500 dark:text-gray-400 mt-1 max-w-[15rem]">{voice.basis}</span>}
            </div>
          )}
        </div>
      )}

      {/* The gap between hours saved and total phone hours IS the transferred calls —
          name it, so the exclusion the reviewer expected to see is stated, not implied. */}
      {!isChat && ts.transferred_conversations != null && ts.transferred_conversations > 0 && (
        <p data-testid="time-saved-transfers" className="gi-body text-gray-500 dark:text-gray-400 mt-3">
          {`${fmtNumber(ts.transferred_conversations)} ${ts.transferred_conversations === 1 ? "call" : "calls"}`}
          {ts.transferred_hours != null ? ` (${fmtNumber(ts.transferred_hours, true)} hrs)` : ""}
          {" transferred to a person — excluded from time saved."}
        </p>
      )}

      {/* Transferred conversations count for nothing here, deliberately — what the
          agent gathered does not reach the person who answers, so they start cold.
          Saying so is what makes the rest of the number credible. */}
      {ts.excluded?.transferred && (
        <p data-testid="time-saved-excluded" className="gi-body text-gray-500 dark:text-gray-400 mt-3">{ts.excluded.transferred}</p>
      )}

      {/* Coverage — the share of calls with a recorded length. At/near 100% it is not
          worth saying; if it drops, the hours are a FLOOR and the panel says so. */}
      {ts.coverage?.pct != null && ts.coverage.pct < 95 && (
        <p data-testid="time-saved-coverage" className="gi-body text-gray-500 dark:text-gray-400 mt-2">
          {ts.coverage.means || `${fmtNumber(ts.coverage.calls_with_duration)} of ${fmtNumber(ts.coverage.calls_total)} calls have a recorded length`}
          {" — hours are a floor."}
        </p>
      )}

      <HourlyRateControl
        id={`${modality}-hourly-rate`}
        defaultRate={defaultRate}
        note={assumptions.note}
        saving={saving}
        onCommit={recompute}
      />
    </PanelShell>
  );
};

export default TimeSavedPanel;
